import React from 'react';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { integrationApiService } from '../../services/integrationApi';
import type { IntegrationConfig } from '../../services/integrationApi';
import { EmptyState, InlineMessage, KpiCard, KpiGrid, LoadingState, SectionCard, StatusChip } from '../ui';

const T = 'integrations.lightspeedEcom.scan';

const scannedAtOf = (value: unknown): Date | null => {
  if (!value) return null;
  if (typeof value === 'string') return new Date(value);
  const maybe = value as { toDate?: () => Date; _seconds?: number; seconds?: number };
  if (typeof maybe.toDate === 'function') return maybe.toDate();
  const seconds = maybe._seconds ?? maybe.seconds;
  return typeof seconds === 'number' ? new Date(seconds * 1000) : null;
};

interface Props {
  integration: IntegrationConfig;
}

/**
 * The read-only link report for a Lightspeed eCom (C-Series) integration: how many eCom
 * products matched the sync selection by article code or barcode, how many are already linked,
 * and what was left on each side. The scan writes nothing; the next sync links the matches and
 * pushes content to them, and resumes from where it stopped.
 */
export const LightspeedEcomScanCard: React.FC<Props> = ({ integration }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['lightspeed-ecom-scan', integration.id];

  const report = useQuery({
    queryKey,
    queryFn: () => integrationApiService.getLightspeedEcomScan(integration.id),
    staleTime: 30_000,
  });

  const scan = useMutation({
    mutationFn: () => integrationApiService.scanLightspeedEcom(integration.id),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      showToast(t(`${T}.done`, { matched: data.stats.matched, products: data.stats.totalProducts }), 'success');
    },
    onError: (error) => showToast((error as Error)?.message || t(`${T}.failed`), 'error'),
  });

  // Which Lightspeed Retail integration wrote this shop's article codes: the scan reads that
  // integration's SKU crosswalk. With several Retail integrations the store picks one here.
  const pairing = report.data?.retailPairing;
  const [retailChoice, setRetailChoice] = React.useState('');
  const pair = useMutation({
    mutationFn: (retailIntegrationId: string) => integrationApiService.setLightspeedEcomRetailPairing(integration.id, retailIntegrationId),
    onSuccess: () => {
      showToast(t(`${T}.pairing.saved`), 'success');
      scan.mutate();
    },
    onError: (error) => showToast((error as Error)?.message || t(`${T}.pairing.failed`), 'error'),
  });
  const pairedName =
    pairing?.retailIntegrationId ? pairing.candidates.find((c) => c.id === pairing.retailIntegrationId)?.name : undefined;

  const stats = report.data?.stats;
  const scannedAt = scannedAtOf(report.data?.scannedAt);
  const cursor = integration.lightspeedEcomSync?.nextOffset ?? null;

  const action = (
    <Button
      variant={stats ? 'outlined' : 'contained'}
      size="small"
      onClick={() => scan.mutate()}
      disabled={scan.isPending}
      startIcon={scan.isPending ? <CircularProgress size={14} /> : undefined}
    >
      {scan.isPending ? t(`${T}.scanning`) : stats ? t(`${T}.rescan`) : t(`${T}.scan`)}
    </Button>
  );

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)} actions={action} dense padded>
      {report.isLoading ? (
        <LoadingState />
      ) : report.isError ? (
        <InlineMessage severity="error">{(report.error as Error)?.message || t(`${T}.failed`)}</InlineMessage>
      ) : !stats ? (
        <EmptyState size="compact" title={t(`${T}.emptyTitle`)} description={t(`${T}.emptyDescription`)} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <StatusChip
              size="small"
              dot
              tone={stats.matched + stats.alreadyLinked > 0 ? 'positive' : 'caution'}
              label={t(`${T}.coverage`, { linked: stats.matched + stats.alreadyLinked, selected: stats.selectedProducts })}
            />
            {scannedAt && (
              <Typography variant="caption" color="text.secondary">
                {t(`${T}.scannedAt`, { date: scannedAt.toLocaleString() })}
              </Typography>
            )}
          </Box>
          <KpiGrid variant="cell" minWidth={140}>
            <KpiCard variant="cell" label={t(`${T}.kpi.matched`)} value={stats.matched} note={t(`${T}.kpi.matchedNote`, { articleCode: stats.matchedByArticleCode, ean: stats.matchedByEan })} tone="positive" />
            <KpiCard variant="cell" label={t(`${T}.kpi.alreadyLinked`)} value={stats.alreadyLinked} />
            <KpiCard variant="cell" label={t(`${T}.kpi.unmatchedProducts`)} value={stats.unmatchedProducts} tone={stats.unmatchedProducts > 0 ? 'caution' : 'neutral'} note={t(`${T}.kpi.unmatchedProductsNote`)} />
            <KpiCard variant="cell" label={t(`${T}.kpi.unmatchedItems`)} value={stats.unmatchedItems} note={t(`${T}.kpi.unmatchedItemsNote`, { total: stats.totalProducts })} />
          </KpiGrid>
          {pairing?.status === 'choose' && (
            <InlineMessage severity="warning">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">{t(`${T}.pairing.choose`)}</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    select
                    size="small"
                    label={t(`${T}.pairing.label`)}
                    value={retailChoice}
                    onChange={(e) => setRetailChoice(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {pairing.candidates.map((candidate) => (
                      <MenuItem key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button size="small" variant="contained" disabled={!retailChoice || pair.isPending} onClick={() => pair.mutate(retailChoice)}>
                    {t(`${T}.pairing.save`)}
                  </Button>
                </Box>
              </Box>
            </InlineMessage>
          )}
          {pairedName && (
            <Typography variant="caption" color="text.secondary">
              {t(`${T}.pairing.paired`, { name: pairedName })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {t(`${T}.nextStep`)}
          </Typography>
          {cursor !== null && (
            <InlineMessage severity="info">{t(`${T}.resume`, { offset: cursor })}</InlineMessage>
          )}
        </Box>
      )}
    </SectionCard>
  );
};

export default LightspeedEcomScanCard;
